import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, NgModule, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AnalyticsBootstrapService } from './core/analytics-bootstrap.service';
import { AuthGuard } from './core/guards/auth-guard.service';
import { AnalyticsInterceptor } from './core/interceptors/analytics.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { PendingWritesInterceptor } from './core/interceptors/pending-writes.interceptor';
import { SsoSwitchInterceptor } from './core/interceptors/sso-switch.interceptor';
import { PageTitleStrategy } from './core/page-title.strategy';
import { AppConfigService } from './core/services/app-config.service';
import { AppNotificationsStoreStoreService } from './core/services/app-notifications-store.service';
import { AppStatsStoreStoreService } from './core/services/app-stats-store.service';
import { SESSION_SCOPED_STATE } from './core/services/session/session-scoped-state';
import { DormotechDirectService } from './features/dormotech-direct/services/dormotech-direct.service';
import { ResearchConsentService } from './features/research-consent/services/research-consent.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

@NgModule({
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],

  providers: [
    // Must resolve BEFORE anything that reads SSO config (AuthInterceptor,
    // login redirects, credential-UI guards). Registered first so app
    // bootstrap blocks on it before any route or interceptor runs.
    provideAppInitializer(() => inject(AppConfigService).load()),
    // Everything that remembers something about the signed-in user, so the
    // session boundary can drop it. Signing out is a route change rather than a
    // page load, so anything held in memory otherwise carries into the next
    // session in the same tab. `useExisting` on purpose: the state to drop is
    // the singleton the rest of the app reads, not a second copy of it.
    {
      provide: SESSION_SCOPED_STATE,
      useExisting: ResearchConsentService,
      multi: true,
    },
    {
      provide: SESSION_SCOPED_STATE,
      useExisting: DormotechDirectService,
      multi: true,
    },
    {
      provide: SESSION_SCOPED_STATE,
      useExisting: AppNotificationsStoreStoreService,
      multi: true,
    },
    {
      provide: SESSION_SCOPED_STATE,
      useExisting: AppStatsStoreStoreService,
      multi: true,
    },
    provideAppInitializer(() => inject(AuthGuard).initialize()),
    // Subscribes to navigations and to the signed-in user, so page views are
    // captured from the very first navigation. The GA tag itself is still only
    // fetched on the first tracked event.
    provideAppInitializer(() => inject(AnalyticsBootstrapService).start()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({}),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: OverlayContainer,
      useClass: FullscreenOverlayContainer,
    },
    // Names the browser tab per route, instead of every page reading "Dormotech".
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategy,
    },
     {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // Sends a stale sign-in or recovery form to sso-fe when SSO was switched
    // on after the page loaded. Separate from AuthInterceptor because that
    // one hands public auth routes straight through without seeing errors.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SsoSwitchInterceptor,
      multi: true,
    },
    // Reports failed and slow requests only. Registered after the auth
    // interceptor so it observes the outcome of a retried request, not the
    // 401 that triggered the token refresh.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AnalyticsInterceptor,
      multi: true,
    },
    // Counts saves still in flight, so a reload for a new version waits for
    // them instead of cutting one off.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PendingWritesInterceptor,
      multi: true,
    },

    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
